"use client";

import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@ui/lib/utils";
import { useTranslations } from "next-intl";
import { useCallback, useRef, useState } from "react";
import clsx from "clsx";
import type { LanguageMetadata } from "../languages/config";
import type { SelectTriggerComponentProps } from "./Select";

import DynamicFlag from "../languages/DynamicFlag";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./Select";

// Memoized SelectItem component to prevent unnecessary re-renders
const LanguageSelectItem = ({
  label,
  value,
  fontVariant,
}: {
  label: string;
  value: string;
  fontVariant: "default" | "new";
}) => (
  <SelectItem
    icon={<DynamicFlag className='rounded-full' locale={value} size={20} />}
    value={value}
    fontVariant={fontVariant}
  >
    {label}
  </SelectItem>
);

LanguageSelectItem.displayName = "LanguageSelectItem";

// Virtualized language list component
const VirtualizedLanguageList = ({
  languages,
  fontVariant,
}: {
  languages: LanguageMetadata[];
  fontVariant: "default" | "new";
}) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: languages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40, // Estimated height of each language item
    overscan: 5, // Number of items to render outside the viewport
  });

  return (
    <div
      className='scrollbar-none overflow-auto'
      ref={parentRef}
      style={{
        height: "250px",
        width: "100%",
        maxWidth: "100%",
        position: "relative",
        minWidth: "200px",
      }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: "100%",
          maxWidth: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {virtualizer.getVirtualItems().map(virtualItem => {
          const { label, value } = languages[virtualItem.index];
          return (
            <div
              key={value}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <LanguageSelectItem label={label} value={value} fontVariant={fontVariant} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const LanguageSelector = ({
  language,
  setSelectedLanguage,
  sortedLanguages,
  showSelectedLabel = false,
  showDropdownLabel = false,
  showDropdownArrow = true,
  fullWidth = false,
  customSelectTrigger,
  triggerSize = "md",
  showFlag = true,
  triggerVariant,
  overlayStyle,
}: {
  customSelectTrigger?: (props: { language: LanguageMetadata | undefined }) => React.ReactNode;
  fullWidth?: boolean;
  language: LanguageMetadata | undefined;
  setSelectedLanguage: (language: string) => void;
  showDropdownArrow?: boolean;
  showDropdownLabel?: boolean;
  showSelectedLabel?: boolean;
  sortedLanguages: LanguageMetadata[];
  triggerSize?: SelectTriggerComponentProps["size"];
  showFlag?: boolean;
  triggerVariant?: "outline-new" | "elevated";
  overlayStyle?: boolean;
}) => {
  const t = useTranslations("ClientKeys.TTS");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = useCallback((open: boolean) => {
    if (open) {
      // Immediate visual feedback
      setIsLoading(true);
      // Use requestAnimationFrame to ensure the loading state is rendered before heavy operations
      requestAnimationFrame(() => {
        setIsOpen(true);
        setIsLoading(false);
      });
    } else {
      setIsOpen(false);
    }
  }, []);

  return (
    <div className='flex flex-col gap-4'>
      {showDropdownLabel && <div className='f-ui-04'>{t("widget.language")}</div>}
      <Select
        onOpenChange={handleOpenChange}
        onValueChange={value => setSelectedLanguage(value)}
        open={isOpen}
        value={language?.value}
      >
        {customSelectTrigger ? (
          customSelectTrigger({ language })
        ) : (
          <SelectTrigger
            className={cn(
              "gap-8 ps-10 transition-all duration-150",
              fullWidth && "w-full",
              isLoading && "scale-[0.98] bg-overlay-lighter",
            )}
            disabled={isLoading}
            showDropdownArrow={showDropdownArrow}
            size={triggerSize}
            variant={triggerVariant}
            overlayStyle={overlayStyle}
          >
            <SelectValue aria-label={language?.label}>
              <div className={clsx("flex items-center gap-8", !showFlag && "pl-12")}>
                {language?.value && showFlag && (
                  <DynamicFlag className='rounded-full' locale={language.value} size={20} />
                )}
                {showSelectedLabel && language?.label}
              </div>
            </SelectValue>
          </SelectTrigger>
        )}
        <SelectContent className='max-h-[350px]'>
          <VirtualizedLanguageList languages={sortedLanguages} fontVariant={triggerSize === "md" ? "new" : "default"} />
        </SelectContent>
      </Select>
    </div>
  );
};
